"use client";

import { useEffect, useState } from "react";

/** Recovery for standalone composition media; iframe load does not attest to a provider's content. */
export function PublicMediaFrame({ src, title, kind = "iframe", allow }: {
  src: string;
  title: string;
  kind?: "iframe" | "video";
  allow?: string;
}) {
  // A source change owns a fresh loading/retry session.
  return <MediaSession key={`${kind}:${src}`} src={src} title={title} kind={kind} allow={allow} />;
}

function MediaSession({ src, title, kind, allow }: { src: string; title: string; kind: "iframe" | "video"; allow?: string }) {
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "slow" | "failed">("loading");
  useEffect(() => {
    if (status !== "loading") return;
    const timeout = setTimeout(() => setStatus("slow"), 15000);
    return () => clearTimeout(timeout);
  }, [attempt, status]);
  const ready = () => setStatus("ready");
  const failed = () => setStatus("failed");
  const retry = () => { setStatus("loading"); setAttempt(value => value + 1); };
  const safeSource = /^(https?:\/\/|\/(?!\/))/i.test(src);
  return <div className="relative h-full w-full bg-black">
    {kind === "video" ? <video key={attempt} src={src} aria-label={title} controls playsInline preload="metadata" onLoadedData={ready} onLoadedMetadata={ready} onError={failed} className="h-full w-full bg-black" /> : <iframe key={attempt} src={src} title={title} className="h-full w-full" allow={allow} allowFullScreen onLoad={ready} onError={failed} />}
    {status === "loading" ? <div role="status" className="pointer-events-none absolute inset-x-0 top-0 bg-black/85 p-3 text-center text-sm text-white">Loading {title.toLowerCase()}…</div> : null}
    {status === "slow" || status === "failed" ? <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/90 p-5 text-center text-white">
      <p role="status" className="max-w-sm text-sm">{status === "failed" ? `${title} could not load.` : `${title} is taking longer than expected.`}</p>
      <div className="flex flex-wrap justify-center gap-2">
        <button type="button" onClick={retry} className="min-h-11 rounded-lg border border-white/30 px-4 text-sm">Retry {title.toLowerCase()}</button>
        {safeSource ? <a href={src} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center rounded-lg border border-white/30 px-4 text-sm">Open {title.toLowerCase()}</a> : null}
      </div>
    </div> : null}
  </div>;
}
